import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { toast } from 'react-toastify';

import { useAuth } from '../../context/AuthContext';
import { representativeService } from '../../services/representativeService';

// Import the new child components
import NoPrivilege from '../../components/common/NoPrivilege';
import RepresentativeHeader from './RepresentativeHeader';
import RepresentativeFilters from './RepresentativeFilters';
import RepresentativesTable from './RepresentativesTable';
import EnhancedPagination from '../../components/common/EnhancedPagination';
import RepresentativeDialog from '../../components/RepresentativeDialog';
import RepresentativeStats from '../../components/RepresentativeStats';

const RepresentativeManager = () => {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();

    // Authorization checks
    const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'representatives' && p.operations.includes('view'));
    const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'representatives' && p.operations.includes('update'));
    const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'representatives' && p.operations.includes('delete'));

    // State management
    const [privilegeError, setPrivilegeError] = useState(false);
    const [representatives, setRepresentatives] = useState([]);
    const [loading, setLoading] = useState(true);

    // Dialog states
    const [dialogOpen, setDialogOpen] = useState(false);
    const [statsDialogOpen, setStatsDialogOpen] = useState(false);
    const [selectedRepresentative, setSelectedRepresentative] = useState(null);

    // Filter and Sort states
    const [filters, setFilters] = useState({
        search: '',
        isActive: 'all',
        sortBy: 'createdAt',
        sortOrder: 'desc',
    });

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1,
    });

    // --- DATA FETCHING ---
    const fetchRepresentatives = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setPrivilegeError(false);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                search: filters.search || undefined,
                isActive: filters.isActive === 'all' ? undefined : filters.isActive === 'active',
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
            };

            // *** THIS IS THE FIX FOR THE API RESPONSE ***
            // The service call now returns { data, pagination } directly.
            const { data, pagination: newPagination } = await representativeService.getAllRepresentatives(params);

            setRepresentatives(data || []);
            setPagination(prev => ({ ...prev, ...newPagination }));

        } catch (error) {
            const backendMessage = error.response?.data?.message;
            if (backendMessage?.toLowerCase().includes('insufficient privileges')) {
                setPrivilegeError(true);
            } else {
                toast.error(t('error.fetchingRepresentatives'));
            }
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filters, canView, t]);

    useEffect(() => {
        fetchRepresentatives();
    }, [fetchRepresentatives]);

    // --- EVENT HANDLERS ---
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on filter change
    };

    const handlePageChange = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleLimitChange = (event) => {
        setPagination(prev => ({ ...prev, limit: parseInt(event.target.value, 10), page: 1 }));
    };

    const handleCreate = () => {
        setSelectedRepresentative(null);
        setDialogOpen(true);
    };

    const handleEdit = (representative) => {
        setSelectedRepresentative(representative);
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('confirm.deleteRepresentative'))) {
            try {
                await representativeService.deleteRepresentative(id);
                toast.success(t('success.representativeDeleted'));
                fetchRepresentatives(); // Refresh data
            } catch (error) {
                toast.error(error.response?.data?.message || t('error.deletingRepresentative'));
            }
        }
    };

    const handleViewStats = (representative) => {
        setSelectedRepresentative(representative);
        setStatsDialogOpen(true);
    };

    const handleDialogClose = (refresh = false) => {
        setDialogOpen(false);
        if (refresh) fetchRepresentatives();
    };

    if (!canView || privilegeError) {
        return <NoPrivilege message={t('representatives.noPrivilegeError')} />;
    }

    return (
        <Box sx={{ p: 3 }}>
            <RepresentativeHeader onCreateClick={handleCreate} />

            <RepresentativeFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                limit={pagination.limit}
                onLimitChange={handleLimitChange}
                onRefresh={fetchRepresentatives}
                loading={loading}
            />

            <RepresentativesTable
                representatives={representatives}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onViewStats={handleViewStats}
                canEdit={canEdit}
                canDelete={canDelete}
            />

            <EnhancedPagination
                pagination={pagination}
                onPageChange={handlePageChange}
            />

            {/* Dialogs remain controlled by the parent */}
            <RepresentativeDialog
                open={dialogOpen}
                representative={selectedRepresentative}
                onClose={handleDialogClose}
            />
            <RepresentativeStats
                open={statsDialogOpen}
                representative={selectedRepresentative}
                onClose={() => setStatsDialogOpen(false)}
            />
        </Box>
    );
};

export default RepresentativeManager;